import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';

import 'rxjs/add/operator/map';

import { UserService } from '../user/user.service';

import { User } from '../user/user.model';
import { RecentlyViewed } from '../user/user-settings.model';

import { UserRole } from './roles.enum';

@Injectable()
export class AuthService {
  keepSubscriptionsAlive: boolean = true;
  currentUser: User;

  private _returnUrl: string = '/dashboard';

  get returnUrl() {
    return this._returnUrl;
  }

  set returnUrl(url) {
    this._returnUrl = (url === '/login' || url === '/') ? '/dashboard' : url;
  }

  constructor(public jwtHelper: JwtHelperService,
              private readonly userService: UserService,
              private readonly router: Router) {
  }

  initialiseLogin() {
    const token = this.getToken();

    if (token && token !== 'null') {
      const decodedUser = this.decodeUserFromToken(token).user;
      this.setCurrentUserFromId(decodedUser);
    }
  }

  saveCurrentUser() {
    this.userService.saveUser(this.currentUser);
  }

  getToken(): string {
    return this.jwtHelper.tokenGetter();
  }

  isAuthenticated(): boolean {
    const token = this.getToken();

    if (token) {
      return !this.jwtHelper.isTokenExpired(token);
    } else {
      return false;
    }
  }

  decodeUserFromToken(userToken) {
    return this.jwtHelper.decodeToken(userToken);
  }

  setCurrentUserFromId(decodedUser): boolean {
    if (decodedUser && !this.currentUser) {
      this.userService
        .getUserFromId(decodedUser._id || decodedUser)
        .takeWhile(() => this.keepSubscriptionsAlive)
        .subscribe(
          response => {
            this.currentUser = response;
            this.returnUrl = this.isExternalUser() && !this.returnUrl.includes('/ext') ? '/ext' + this.returnUrl : this.returnUrl;
            this.router.navigate([ this.returnUrl ]);
          },
          error => console.log('error getting extra user data', error)
        );
    } else {
      return false;
    }
  }

  clearUser() {
    localStorage.removeItem('userToken');
    delete this.currentUser;
  }

  isSuperAdmin() {
    return this.isLoggedIn() && this.currentUser.role >= UserRole.superadmin;
  }

  isAdmin() {
    return this.isLoggedIn() && this.currentUser.role >= UserRole.admin;
  }

  isManager() {
    return this.isLoggedIn() && this.currentUser.role >= UserRole.manager;
  }

  isUser() {
    return this.isLoggedIn() && this.currentUser.role >= UserRole.user;
  }

  isExternalUser() {
    return this.isLoggedIn() && this.currentUser.role === UserRole.external;
  }

  isClient() {
    return this.isLoggedIn() && this.currentUser.role === UserRole.client;
  }

  isLoggedIn() {
    return (this.currentUser !== undefined);
  }

  addItemToRecentlyViewed(viewedItem: RecentlyViewed) {
    // initially if the array doesn't exist then add it
    if (this.currentUser.userSettings.recentlyViewedItems === undefined) {
      this.currentUser.userSettings.recentlyViewedItems = [];
    }

    // remove it from the list if it already exists
    this.currentUser.userSettings.recentlyViewedItems = this.currentUser.userSettings.recentlyViewedItems.filter(rvi => rvi.objectId !== viewedItem.objectId);

    // add the object to first place in the array and then reduce to 10
    this.currentUser.userSettings.recentlyViewedItems.unshift(viewedItem);
    this.currentUser.userSettings.recentlyViewedItems.slice(0, 10);
    this.saveCurrentUser();
  }
}



// WEBPACK FOOTER //
// ./client/app/core/auth/auth.service.ts